
import React from "react";
import { runGlobalSearch, type SearchHit } from "../search/globalSearch";
import type { NavKey } from "../Sidebar";
import { Modal } from "./Modal";

type Props = {
  onNavigate: (key: NavKey, query?: string) => void;
};

export function GlobalSearchBar({ onNavigate }: Props) {
  const [query, setQuery] = React.useState("");
  const [results, setResults] = React.useState<SearchHit[]>([]);
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  
  // Advanced Filter State
  const [dateFrom, setDateFrom] = React.useState("");
  const [dateTo, setDateTo] = React.useState("");
  
  const inputRef = React.useRef<HTMLInputElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (query) {
        // Pass date range to search logic
        setResults(runGlobalSearch(query, dateFrom, dateTo));
        setShowDropdown(true);
      } else {
        setResults([]);
        setShowDropdown(false);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [query, dateFrom, dateTo]);

  // Click outside
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) && 
          inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (hit: SearchHit) => {
    setQuery("");
    setShowDropdown(false);
    onNavigate(hit.navKey, hit.sourceId); 
  };

  const applyAdvancedSearch = () => {
      if (query) {
          setResults(runGlobalSearch(query, dateFrom, dateTo));
          setShowDropdown(true);
      }
      setShowAdvanced(false);
  };

  return (
    <div style={{ position: "relative", width: "100%", maxWidth: "720px", margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", background: "#0b0b0c", border: "1px solid var(--border)", borderRadius: 8, padding: "0 12px", transition: "border-color 0.2s" }}>
        <span style={{ fontSize: 16, marginRight: 10 }}>🔍</span>
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="搜尋合約、案件、條款... (按 '/' 聚焦)"
          style={{ 
            background: "transparent", border: "none", color: "#fff", 
            padding: "12px 0", width: "100%", outline: "none", fontSize: 15 
          }}
          onFocus={() => { if (query) setShowDropdown(true); }}
        />
        <button 
          onClick={() => setShowAdvanced(true)}
          style={{ 
            background: "transparent", border: "none", color: "var(--muted)", 
            cursor: "pointer", fontSize: 13, whiteSpace: "nowrap", paddingLeft: 12, borderLeft: "1px solid #333" 
          }}
        >
          進階
        </button>
      </div>

      {showDropdown && results.length > 0 && (
        <div ref={dropdownRef} style={{
          position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4,
          background: "#1a1d24", border: "1px solid var(--border)", borderRadius: 8,
          boxShadow: "0 10px 30px rgba(0,0,0,0.5)", zIndex: 1000, maxHeight: 400, overflowY: "auto"
        }}>
          {results.map((hit) => (
            <div 
              key={hit.id}
              onClick={() => handleSelect(hit)}
              style={{ padding: "10px 14px", borderBottom: "1px solid #333", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#2a2d36"}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
            >
              <span className="badge" style={{ fontSize: 11, minWidth: 64, textAlign: "center" }}>{hit.type}</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: "bold", color: "#fff" }}>{hit.title}</div>
                {hit.subtitle && <div style={{ fontSize: 12, color: "var(--muted)" }}>{hit.subtitle}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdvanced && (
        <Modal title="進階搜尋" onClose={() => setShowAdvanced(false)}>
           <div className="p" style={{ marginBottom: 12 }}>資料範圍：</div>
           <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
             {["合約", "案件", "爭議", "品牌資產", "顧問律師", "公司治理", "範本", "待辦"].map(t => (
               <label key={t} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                 <input type="checkbox" defaultChecked /> {t}
               </label>
             ))}
           </div>
           
           <div className="p" style={{ marginTop: 20, marginBottom: 10 }}>日期範圍 (建立日/簽約日/發生日)：</div>
           <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input type="date" className="input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
              <span style={{ color: "var(--muted)" }}>~</span>
              <input type="date" className="input" value={dateTo} onChange={e => setDateTo(e.target.value)} />
           </div>

           <div className="p" style={{ marginTop: 20, marginBottom: 10 }}>搜尋欄位：</div>
           <div className="row">
             <label style={{ display: "flex", gap: 8, alignItems: "center" }}><input type="radio" name="f" defaultChecked /> 全部欄位</label>
             <label style={{ display: "flex", gap: 8, alignItems: "center" }}><input type="radio" name="f" /> 僅搜尋標題</label>
           </div>
           
           <div className="row" style={{ justifyContent: "flex-end", marginTop: 20 }}>
             <button className="btn" style={{ width: "auto" }} onClick={applyAdvancedSearch}>確認篩選</button>
           </div>
        </Modal>
      )}
    </div>
  );
}
